import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { db, functions } from '@shared/services/firebase';
import { useAuth } from '../context/AuthContext';
import { checkIntroEligibility } from '../services/purchasesService';

/**
 * Trial eligibility SEAM (card A1 ↔ card T5) — the read side the paywall
 * consumes to decide whether it may promise a free trial.
 *
 * Who decides differs by platform, and getting this wrong lies to the user
 * (see `docs/operations/2026/Q3/IOS_TRIAL_OFFERS.md`):
 *
 * - **iOS — Apple decides.** The trial is an App Store Connect INTRODUCTORY
 *   offer, applied automatically at purchase to Apple IDs Apple considers
 *   eligible (once per Apple ID, per subscription group). We cannot grant or
 *   withhold it, so `freeTrialUsed` does NOT gate here. What we CAN do — and
 *   must — is ask whether this Apple ID is eligible before showing trial copy:
 *   `product.introPrice` is static metadata present regardless of the person,
 *   so rendering off it alone would promise a free month to a returning user
 *   and then charge them immediately. Fails CLOSED: anything short of a
 *   confirmed ELIGIBLE shows plain pricing.
 * - **Android — Play decides.** `checkTrialOrIntroductoryPriceEligibility`
 *   always returns UNKNOWN on Android, so applying the fail-closed rule here
 *   would hide the trial from every Android user. The package's own free phase
 *   (`extractTrial`) remains the signal, exactly as before.
 * - **Web — we decide.** Stripe has no native trial eligibility whatsoever, so
 *   the `freeTrialUsed` flag on `userContent/{uid}` is the only thing stopping
 *   unlimited repeat trials. Two signals gate the copy, and like iOS both must
 *   confirm before we promise anything (fail closed while unresolved):
 *   the `freeTrialUsed` snapshot, and `enough_check_trial_eligibility` — a
 *   server precheck that also consults the deleted-email registry the client
 *   can never read (hash-only, server-side), stamping `freeTrialUsed` as a
 *   side effect when it matches so the snapshot self-corrects. Without the
 *   precheck, an anonymous-first signup with a marked email would see trial
 *   copy the server then refuses at checkout — a promise we can't walk back.
 *   The server (`requestTrial` → `trialApplied`) stays authoritative for the
 *   actual grant; both signals fail OPEN on error (unlike pending) because
 *   wrongly hiding the trial from a legit user is the worse failure.
 *
 * Clients can read `freeTrialUsed` but never write it (see firestore.rules).
 */
export interface TrialEligibility {
  /**
   * True when we may honestly show trial terms for `productId`. Pass the
   * product the user is actually about to buy — on iOS, eligibility is per
   * product, so monthly and annual can differ.
   */
  isTrialAvailableFor: (productId?: string | null) => boolean;
  /** True while the underlying signal is still resolving. */
  loading: boolean;
}

/**
 * @param productIds Store product identifiers to resolve iOS eligibility for.
 *   Ignored on Android/web. Pass the offering's products once they're loaded.
 */
export function useTrialEligibility(productIds: string[] = []): TrialEligibility {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;

  const isNative = Capacitor.isNativePlatform();
  const isIOS = isNative && Capacitor.getPlatform() === 'ios';
  const isAndroid = isNative && Capacitor.getPlatform() === 'android';

  const [freeTrialUsed, setFreeTrialUsed] = useState(false);
  const [webLoading, setWebLoading] = useState(!!uid && !isNative);
  // Server precheck answer: null = still resolving (fail closed), true/false
  // once `enough_check_trial_eligibility` has answered (errors resolve open).
  const [serverAllowed, setServerAllowed] = useState<boolean | null>(null);

  // Stable key so a fresh array identity each render doesn't re-fire the check.
  const productKey = useMemo(() => [...productIds].sort().join('|'), [productIds]);
  const [introEligible, setIntroEligible] = useState<Record<string, boolean> | null>(null);
  const [introLoading, setIntroLoading] = useState(false);

  // --- iOS: ask Apple, per product ------------------------------------------
  useEffect(() => {
    if (!isIOS || !productKey) return;

    let cancelled = false;
    setIntroLoading(true);
    checkIntroEligibility(productKey.split('|'))
      .then((map) => {
        if (cancelled) return;
        setIntroEligible(map);
        setIntroLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed — never promise a trial we couldn't confirm.
        setIntroEligible({});
        setIntroLoading(false);
      });

    return () => { cancelled = true; };
  }, [isIOS, productKey]);

  // --- Web: our own flag is the gate ----------------------------------------
  useEffect(() => {
    if (isNative) return;

    if (!uid) {
      // Signed-out: match the prior optimistic semantics (absent → eligible)
      // rather than blocking on auth state this hook doesn't own — the
      // paywall itself requires an authenticated user before it ever opens.
      setFreeTrialUsed(false);
      setWebLoading(false);
      return;
    }

    setWebLoading(true);
    const unsub = onSnapshot(
      doc(db, 'userContent', uid),
      (snap) => {
        setFreeTrialUsed(!!snap.data()?.freeTrialUsed);
        setWebLoading(false);
      },
      () => {
        // Fail open: a read error shouldn't wedge the paywall in "loading"
        // forever, and unlike iOS the server still gates this for real before
        // anything is granted (`requestTrial` → `trialApplied`), so the worst
        // case is copy that the success screen then corrects.
        setFreeTrialUsed(false);
        setWebLoading(false);
      },
    );
    return unsub;
  }, [isNative, uid]);

  // --- Web: server precheck — the registry check the client can't do --------
  useEffect(() => {
    if (isNative || !uid) return;

    let cancelled = false;
    setServerAllowed(null);
    httpsCallable<Record<string, never>, { trialAllowed?: boolean }>(
      functions,
      'enough_check_trial_eligibility',
    )({})
      .then((res) => {
        if (cancelled) return;
        setServerAllowed(res.data?.trialAllowed !== false);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open — same posture as the snapshot error path above: the
        // server still gates the actual grant at checkout.
        setServerAllowed(true);
      });
    return () => { cancelled = true; };
  }, [isNative, uid]);

  const isTrialAvailableFor = useCallback((productId?: string | null): boolean => {
    if (isIOS) {
      // Fail closed: unresolved, absent from the map, or any status other than
      // a confirmed ELIGIBLE means we show plain pricing.
      if (!productId || introEligible === null) return false;
      return introEligible[productId] === true;
    }
    if (isAndroid) {
      // Play gates natively and the eligibility API can't answer here; the
      // package's own free phase decides whether trial copy renders.
      return true;
    }
    // Signed-out keeps the prior optimistic semantics (the paywall requires
    // auth before opening); signed-in fails closed until BOTH the snapshot
    // and the server precheck have confirmed.
    if (!uid) return true;
    return !freeTrialUsed && serverAllowed === true;
  }, [isIOS, isAndroid, introEligible, freeTrialUsed, uid, serverAllowed]);

  return {
    isTrialAvailableFor,
    loading: isIOS
      ? introLoading
      : (isNative ? false : (webLoading || (!!uid && serverAllowed === null))),
  };
}
